import axios, { AxiosResponse, AxiosError } from 'axios'
import { useUserStore } from '@/store/user';
import router from '@/router';
import { ElMessage } from 'element-plus';
import { isMobileDevice } from '@/utils/index';
import { showFailToast } from 'vant';
import { isMerchantPath, isMerchantRole, MERCHANT_LOGIN } from '@/utils/merchantAccess';

declare module 'axios' {
  export interface AxiosRequestConfig<D = any> { silentError?: boolean }
}

function notifyError(message: string) {
  if (isMobileDevice()) {
    showFailToast({ message, duration: 2500, forbidClick: true });
  } else {
    ElMessage.error(message);
  }
}

/** 账号管理端共用客户端：统一返回业务响应体，并按手机／PC 选择提示样式；电视设备接口应使用独立客户端。 */
const request = axios.create({
  timeout: 50 * 1000,
  // baseURL: process.env.VUE_APP_BASE_API,
})

request.interceptors.response.use(
  function (config: AxiosResponse<any>) {
    return config.data
  },
  function (error: any) {
    let errorMessage = ''
    const passwordFailure = String(error.config?.url || '').endsWith('/user/updatePassword')
    /** 静默业务错误不能隐藏会话失效；修改密码的校验失败单独处理，避免输错旧密码时被跳回登录。 */
    const silent = !!error.config?.silentError && (passwordFailure || ![401, 403].includes(error.response?.status))
    if (error.response) {
      const merchant = isMerchantPath(router.currentRoute.value.path) || isMerchantRole(useUserStore().userInfo.roleInfo?.roleIdentifier)
      const wrongPassword = passwordFailure
      const businessDenied = !!error.response.data?.code && error.response.status === 403
      if ((error.response.status === 401 || (!merchant && error.response.status === 403 && !businessDenied)) && !wrongPassword) {
        errorMessage = error.response.data?.message || '登录状态已失效，请重新登录'
        if (!silent) notifyError(errorMessage)
        const targetLoginPath = merchant ? MERCHANT_LOGIN : isMobileDevice() ? '/mlogin' : '/home/login';
        if (merchant) useUserStore().resetSession()
        router.push({ path: targetLoginPath, ...(merchant ? { query: { redirect: router.currentRoute.value.fullPath } } : {}) });
      } else {
        errorMessage = error.response.data?.message || '请求失败，请稍后再试'
        if (!silent) notifyError(errorMessage)
      }
    } else if (error.request) {
      errorMessage = '网络连接异常，请检查网络后重试'
      if (!silent) notifyError(errorMessage)
    } else {
      errorMessage = '请求发送失败，请稍后再试'
      if (!silent) notifyError(errorMessage)
    }

    error.userMessage = errorMessage
    error.notified = !silent
    return Promise.reject(error)
  }
)
// 请求拦截器
request.interceptors.request.use(
  (config) => {
    const userStore = useUserStore();
    // 每次发送时读取当前账号令牌，避免切换账号后继续使用创建客户端时捕获的旧值。
    const token = userStore.token;
    // console.log(token);

    if (token) {
      config.headers['x-access-token'] = token;
    }
    // console.log(config);

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default request
